import { useEffect, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { Link } from 'react-router-dom';

interface DocumentTraceJob {
  id: string;
  name: string;
  status: string;
  parentJobId: string | null;
  createdAt: number;
  completedAt?: number;
  data: any;
}

interface DocumentTraceViewProps {
  traceId: string;
}

// Document processing pipeline stages with enhanced metadata
const DOCUMENT_PIPELINE_STAGES = [
  { 
    key: 'upload', 
    label: 'Upload', 
    icon: '📤',
    jobTypes: ['DocumentUploadIngestJob'],
    description: 'File uploaded to system'
  },
  { 
    key: 'ingest', 
    label: 'Ingest', 
    icon: '📥',
    jobTypes: ['DocumentIngestJob', 'DocumentUploadIngestJob'],
    description: 'Document processed and stored'
  },
  { 
    key: 'extract', 
    label: 'Extract', 
    icon: '📝',
    jobTypes: ['TextExtractionJob'],
    description: 'Text extracted from document'
  },
  { 
    key: 'metadata', 
    label: 'Metadata', 
    icon: '🏷️',
    jobTypes: ['MetadataExtractionJob'],
    description: 'Metadata analyzed and extracted'
  },
  { 
    key: 'vectorize', 
    label: 'Vectorize', 
    icon: '🔍',
    jobTypes: ['DocumentVectorizationJob'],
    description: 'Document vectorized for search'
  },
  { 
    key: 'complete', 
    label: 'Available', 
    icon: '✅',
    jobTypes: [],
    description: 'Document ready in chat'
  }
];

const FILE_TYPE_ICONS: Record<string, string> = {
  'pdf': '📄',
  'doc': '📝',
  'docx': '📝',
  'txt': '📋',
  'rtf': '📝',
  'odt': '📝',
  'jpg': '🖼️',
  'jpeg': '🖼️',
  'png': '🖼️',
  'gif': '🖼️',
  'bmp': '🖼️',
  'tiff': '🖼️',
  'unknown': '📎'
};

const isDocumentJob = (jobName?: string) => {
  if (!jobName) return false;
  return DOCUMENT_PIPELINE_STAGES.some(stage => 
    stage.jobTypes.some(type => jobName.includes(type))
  );
};

const getFileTypeIcon = (fileType?: string) => {
  if (!fileType) return FILE_TYPE_ICONS.unknown;
  return FILE_TYPE_ICONS[fileType.toLowerCase()] || FILE_TYPE_ICONS.unknown;
};

const formatFileSize = (bytes?: number) => {
  if (!bytes) return '';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

const formatDuration = (startMs: number, endMs?: number) => {
  if (!endMs) return 'In progress...';
  const durationMs = endMs - startMs;
  if (durationMs < 1000) return `${durationMs}ms`;
  const seconds = Math.round(durationMs / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  return `${minutes}m`;
};

export default function DocumentTraceView({ traceId }: DocumentTraceViewProps) {
  const { keycloak } = useKeycloak();
  const [jobs, setJobs] = useState<DocumentTraceJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTrace = async () => {
      if (!keycloak?.token) return;

      try {
        const res = await fetch('/api/v1/paralex-mcp/tools/get_job_trace', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${keycloak.token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ jobId: traceId })
        });

        if (!res.ok) throw new Error(`Server responded with ${res.status}`);
        const response = await res.json();
        const data = response.result || response;
        setJobs(data.jobs || []);
        setError(null);
      } catch (err: any) {
        console.error('Failed to load job trace:', err);
        setError(err.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    loadTrace();
  }, [traceId, keycloak.token]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-lex-accent"></div>
        <span>Loading document trace...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
        <p className="text-red-700 dark:text-red-300">⚠️ Error loading trace: {error}</p>
      </div>
    );
  }

  if (!jobs.length) {
    return (
      <div className="mt-6">
        <p className="text-gray-500 dark:text-gray-400 italic mb-6">
          No jobs found for trace <strong>{traceId}</strong>
        </p>
        
        <div className="p-4 bg-lex-accent-soft rounded-lg border border-lex-accent">
          <p className="text-lex-accent text-sm">
            💡 <strong>Need help analyzing this trace?</strong> Use the ParaLex System Chat from the main System Monitor page.
          </p>
        </div>
      </div>
    );
  }

  // Check if this is a document processing trace
  const documentJobs = jobs.filter(job => isDocumentJob(job.name));
  const isDocumentTrace = documentJobs.length > 0;

  if (!isDocumentTrace) {
    // Fall back to standard trace view for non-document traces
    return (
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Standard Trace View</h3>
        <div className="space-y-2">
          {jobs.map(job => (
            <div key={job.id} className="p-3 border border-gray-200 dark:border-gray-700 rounded">
              <div className="flex items-center justify-between">
                <span className="font-medium">{job.name}</span>
                <span className={`px-2 py-1 rounded text-xs ${
                  job.status === 'completed' ? 'bg-green-100 text-green-800' :
                  job.status === 'failed' ? 'bg-red-100 text-red-800' :
                  'bg-lex-accent-soft text-lex-accent'
                }`}>
                  {job.status}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {new Date(job.createdAt).toLocaleString()}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
          <p className="text-purple-800 dark:text-purple-200 text-sm">
            🤖 <strong>ParaLex System Agent:</strong> For AI-powered trace analysis, use the ParaLex chat from the System Monitor.
          </p>
        </div>
      </div>
    );
  }

  // Extract document metadata from the first job with document info
  const documentJob = documentJobs[0];
  const documentId = documentJob?.data?._meta?.documentId || documentJob?.data?.documentId;
  const fileName = documentJob?.data?._meta?.fileName || documentJob?.data?.fileName || documentJob?.data?._meta?.filename;
  const fileType = documentJob?.data?._meta?.fileType || documentJob?.data?.fileType;
  const fileSize = documentJob?.data?._meta?.fileSize || documentJob?.data?.fileSize;

  // Map jobs to pipeline stages
  const stageJobs = DOCUMENT_PIPELINE_STAGES.map(stage => {
    const job = jobs.find(j => stage.jobTypes.some(type => j.name.includes(type)));
    return {
      ...stage,
      job: job || null,
      completed: job?.status === 'completed',
      failed: job?.status === 'failed',
      active: job?.status === 'active' || job?.status === 'running'
    };
  });

  // Determine overall completion
  const completedStages = stageJobs.filter(s => s.completed).length;
  const hasFailures = stageJobs.some(s => s.failed);

  return (
    <div className="mt-6">
      <div className="bg-gradient-to-br from-green-50 to-lex-accent-soft dark:from-green-900/10 dark:to-lex-accent-soft rounded-lg border border-green-200 dark:border-green-800 p-6">
        {/* Document Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="text-4xl">
              {getFileTypeIcon(fileType)}
            </div>
            <div>
              <h3 className="text-xl font-semibold text-green-800 dark:text-green-200">
                📄 Document Processing Pipeline
              </h3>
              {fileName && (
                <p className="text-green-700 dark:text-green-300 font-medium">
                  {fileName}
                </p>
              )}
              <div className="flex gap-4 text-sm text-gray-600 dark:text-gray-400 mt-1">
                {fileType && <span>Type: {fileType.toUpperCase()}</span>}
                {fileSize && <span>Size: {formatFileSize(fileSize)}</span>}
                <span>Stages: {completedStages}/{DOCUMENT_PIPELINE_STAGES.length}</span>
              </div>
            </div>
          </div>
          
          {documentId && (
            <a
              href={`/chat?documentId=${documentId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              💬 Jump to Chat
            </a>
          )}
        </div>

        {/* Pipeline Visualization */}
        <div className="relative">
          <div className="flex items-center justify-between relative">
            {stageJobs.map((stage, index) => (
              <div key={stage.key} className="flex flex-col items-center relative z-10">
                {/* Stage Circle */}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl border-2 transition-all duration-300 ${
                  stage.failed 
                    ? 'bg-red-500 border-red-500 text-white' 
                    : stage.completed
                    ? 'bg-green-500 border-green-500 text-white shadow-lg'
                    : stage.active
                    ? 'bg-lex-accent border-lex-accent text-lex-accent-contrast animate-pulse'
                    : 'bg-gray-200 border-gray-300 text-gray-500 dark:bg-gray-700 dark:border-gray-600'
                }`}>
                  {stage.failed ? '❌' : stage.icon}
                </div>
                
                {/* Stage Label */}
                <span className={`text-sm mt-2 font-medium text-center ${
                  stage.completed || stage.active
                    ? 'text-green-700 dark:text-green-300'
                    : stage.failed
                    ? 'text-red-700 dark:text-red-300' 
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {stage.label}
                </span>
                
                {/* Stage Description */}
                <span className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1 max-w-20">
                  {stage.description}
                </span>
                
                {/* Job Details */}
                {stage.job && (
                  <div className="mt-2 p-2 bg-white dark:bg-gray-800 rounded border shadow-sm min-w-32">
                    <div className="text-xs text-center">
                      <Link
                        to={`/system-monitor/job-trace/${stage.job.id}`}
                        className="text-lex-accent hover:underline font-medium"
                      >
                        View Job
                      </Link>
                      <div className="text-gray-500 dark:text-gray-400 mt-1">
                        {formatDuration(stage.job.createdAt, stage.job.completedAt)}
                      </div>
                      <div className="text-gray-400 dark:text-gray-500 text-xs">
                        {new Date(stage.job.createdAt).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Connection Line */}
                {index < stageJobs.length - 1 && (
                  <div className={`absolute top-6 left-6 w-full h-0.5 ${
                    stage.completed && !stageJobs[index + 1].failed
                      ? 'bg-green-500' 
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`} style={{ width: 'calc(100% - 24px)' }} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Summary Status */}
        <div className="mt-6 p-4 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white">Pipeline Status</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {hasFailures 
                  ? '❌ Pipeline has failures - check individual stages'
                  : completedStages === DOCUMENT_PIPELINE_STAGES.length
                  ? '✅ Document processing completed successfully'
                  : `⏳ Processing... (${completedStages}/${DOCUMENT_PIPELINE_STAGES.length} stages complete)`
                }
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {Math.round((completedStages / DOCUMENT_PIPELINE_STAGES.length) * 100)}%
              </div>
              <div className="text-xs text-gray-500">Complete</div>
            </div>
          </div>
        </div>

        {/* Other Jobs in Trace */}
        {jobs.filter(job => !isDocumentJob(job.name)).length > 0 && (
          <div className="mt-6">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Other Jobs in Trace</h4>
            <div className="space-y-2">
              {jobs.filter(job => !isDocumentJob(job.name)).map(job => (
                <div key={job.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                  <div>
                    <span className="font-medium">{job.name}</span>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(job.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      job.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                      job.status === 'failed' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                      'bg-lex-accent-soft text-lex-accent'
                    }`}>
                      {job.status}
                    </span>
                    <Link
                      to={`/system-monitor/job-trace/${job.id}`}
                      className="text-lex-accent hover:underline text-sm"
                    >
                      View
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-lex-accent-soft dark:from-purple-900/20 dark:to-lex-accent-soft rounded-lg border border-purple-200 dark:border-purple-700">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🤖</span>
          <div>
            <h4 className="font-semibold text-purple-800 dark:text-purple-200">ParaLex System Agent</h4>
            <p className="text-purple-700 dark:text-purple-300 text-sm">
              For AI-powered analysis of this document trace, use the ParaLex chat from the main System Monitor page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 